/**
 * BottomTabBar — fixed-bottom 5-tab nav for phone breakpoint (#704 PR-2).
 *
 * Renders only when useBreakpoint() === 'phone'. On tablet/desktop the
 * existing top Navbar is the only nav surface; on phone the top Navbar
 * collapses to a logo-only header and this bar becomes the primary
 * navigation.
 *
 * Tabs:
 *   Map     -> /              (magnifier icon)
 *   Picks   -> /opportunities (star)
 *   Saved   -> /saved         (bookmark) — badge from bookmark count
 *   Alerts  -> /watchlists    (bell)     — badge from useAlertsWebSocket
 *   More    -> opens MoreSheet (grid/dots)
 *
 * Height: 56px + env(safe-area-inset-bottom) so the bar clears the
 * iPhone home indicator. The 56px figure matches Apple HIG / Material
 * Design tab-bar minimums and the existing tablet `.search-page`
 * height calc that subtracts 56px for the navbar.
 *
 * Active-tab detection uses react-router's useLocation and matches a
 * route prefix so /opportunities/anything still highlights "Picks".
 * The "More" tab is active whenever the current path is NOT one of the
 * four primary tab routes (so the sheet contents feel "in scope" when
 * the user is on a More-linked screen like /properties or /dashboard).
 *
 * Active tab styling: brand accent (#e94560) on both icon + label.
 * Inactive: #888. Background: #1a1a2e (matches the existing navbar).
 *
 * Badge sources are passed in as props so this component stays purely
 * presentational and the parent (App.js) controls polling / caching:
 *   - savedCount   (Number) — bookmarks
 *   - alertsCount  (Number) — unread alerts
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

// Inline SVGs to avoid adding an icon-library dep. Each icon is 24x24,
// uses currentColor so the active/inactive state is driven by the
// surrounding CSS color, and is `aria-hidden` because the visible
// label below already describes the tab.
function IconMap() {
  return (
    <svg
      width="24" height="24" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg
      width="24" height="24" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function IconBookmark() {
  return (
    <svg
      width="24" height="24" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconBell() {
  return (
    <svg
      width="24" height="24" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function IconMore() {
  return (
    <svg
      width="24" height="24" viewBox="0 0 24 24"
      fill="currentColor" stroke="none"
      aria-hidden="true"
    >
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

// Tab definitions. Order is left-to-right in the bar. The "More" tab
// has no `path` because it triggers a sheet, not a route change.
const TABS = [
  { key: 'map',    label: 'Map',    path: '/',              Icon: IconMap },
  { key: 'picks',  label: 'Picks',  path: '/opportunities', Icon: IconStar },
  { key: 'saved',  label: 'Saved',  path: '/saved',         Icon: IconBookmark, badgeFrom: 'saved' },
  { key: 'alerts', label: 'Alerts', path: '/watchlists',    Icon: IconBell,     badgeFrom: 'alerts' },
  { key: 'more',   label: 'More',                            Icon: IconMore,     isMore: true },
];

// True if `pathname` is on (or under) `tabPath`. Treats "/" specially
// so that /parcel/123 doesn't keep the Map tab lit when the user
// navigates deeper — Map is only active on the exact root.
function isPathMatch(pathname, tabPath) {
  if (tabPath === '/') return pathname === '/';
  return pathname === tabPath || pathname.startsWith(tabPath + '/');
}

function BottomTabBar({ savedCount = 0, alertsCount = 0, onMoreClick }) {
  const location = useLocation();
  const pathname = location.pathname;

  // The four primary tabs each own a route family. "More" is the
  // catch-all and gets the active tint whenever the current route
  // isn't claimed by another tab.
  const primaryTabs = TABS.filter((t) => !t.isMore);
  const activePrimary = primaryTabs.find((t) => isPathMatch(pathname, t.path));
  const moreActive = !activePrimary;

  const badgeFor = (key) => {
    if (key === 'saved') return savedCount;
    if (key === 'alerts') return alertsCount;
    return 0;
  };

  return (
    <nav
      className="bottom-tab-bar"
      role="navigation"
      aria-label="Primary mobile navigation"
      data-testid="bottom-tab-bar"
    >
      {TABS.map((tab) => {
        const { Icon } = tab;
        const isActive = tab.isMore
          ? moreActive
          : activePrimary && activePrimary.key === tab.key;
        const count = tab.badgeFrom ? badgeFor(tab.badgeFrom) : 0;
        const showBadge = count > 0;
        const badgeText = count > 999 ? '999+' : String(count);

        const inner = (
          <>
            <span className="bottom-tab-icon" aria-hidden="true">
              <Icon />
              {showBadge && (
                <span
                  className="bottom-tab-badge"
                  data-testid={`bottom-tab-badge-${tab.key}`}
                >
                  {badgeText}
                </span>
              )}
            </span>
            <span className="bottom-tab-label">{tab.label}</span>
          </>
        );

        const classes = `bottom-tab ${isActive ? 'bottom-tab--active' : ''}`;
        const ariaProps = {
          'aria-current': isActive ? 'page' : undefined,
          'aria-label':
            tab.label + (showBadge ? `, ${count} unread` : ''),
        };

        if (tab.isMore) {
          return (
            <button
              key={tab.key}
              type="button"
              className={classes}
              onClick={onMoreClick}
              data-testid={`bottom-tab-${tab.key}`}
              {...ariaProps}
            >
              {inner}
            </button>
          );
        }

        return (
          <Link
            key={tab.key}
            to={tab.path}
            className={classes}
            data-testid={`bottom-tab-${tab.key}`}
            {...ariaProps}
          >
            {inner}
          </Link>
        );
      })}
    </nav>
  );
}

export default BottomTabBar;
